import React, { useCallback, useMemo, useState } from 'react';
import { Button, Empty, Space } from 'antd';
import { Column, DataTable, Spinner, Text, YColumnsType } from '@ydistri/ds';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import { useGetStoresQuery } from '../../../../apis/apiLists';
import {
  EntityColumns,
  useStoreTableColumnsProvider,
} from '../../../../hooks/useEntityTableColumnsProvider';
import { StoreResponse } from '@ydistri/api-sdk';
import { useForceAsoStoreTiersMutation, useGetAsoStoreTiersQuery } from '../../apiAso';
import { useStableParams } from '../../../../routes/useStableParams';
import StoreTierSetter from '../StoreTierSetter/StoreTierSetter';
import StoreTierSelector from '../StoreTierSelector/StoreTierSelector';
import { formatNumber } from '@ydistri/generic-utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useAsoScenarioStatus } from '../../hooks/useAsoScenarioStatus';
import AsoTooltip from '../../AsoTooltip';

interface StoreTierRow {
  store: Pick<StoreResponse, 'customerStoreId' | 'name' | 'code' | 'id'>;
  computedTier: number;
  forcedTier?: number;
  saleValue?: number;
  percentage?: number;
}

const AsoStoreTierTable: React.FC = () => {
  const { asoScenarioId } = useStableParams();
  const currency = useCurrency();
  const { isLocked } = useAsoScenarioStatus();
  const modificationEnabled = asoScenarioId && !isLocked;

  const [bulkTier, setBulkTier] = useState<number>(1);
  const [forceTiers, { isLoading: isForcing }] = useForceAsoStoreTiersMutation();

  const templateOrCalculation = useTemplateOrCalculation();
  const { data: storesData } = useGetStoresQuery(templateOrCalculation);
  const storeColumnsProvider = useStoreTableColumnsProvider();

  const {
    data: tiers = [],
    isFetching: isFetchingTiers,
    isError: isErrorTiers,
  } = useGetAsoStoreTiersQuery(asoScenarioId ?? 0, {
    skip: !asoScenarioId,
  });

  const data: StoreTierRow[] = useMemo(() => {
    // Prepare a stable-sorted list:
    // 1) Rows with forcedTier first
    // 2) Ascending by (forcedTier ?? computedTier)
    // 3) Stable for ties (original order)
    const withIndex = tiers
      .toSorted((a, b) => (b.saleValue ?? 0) - (a.saleValue ?? 0))
      .map((t, idx) => ({ t, idx }))
      .sort((a, b) => {
        const aVal = a.t.forcedTier ?? a.t.computedTier;
        const bVal = b.t.forcedTier ?? b.t.computedTier;
        if (aVal !== bVal) return aVal - bVal; // ascending

        const aForced = !!a.t.forcedTier;
        const bForced = !!b.t.forcedTier;
        if (aForced !== bForced) return aForced ? -1 : 1; // forced first

        return a.idx - b.idx; // keep original order on ties (stable)
      });

    return withIndex.map(({ t, idx }) => {
      const store = storesData?.object[t.storeId];
      return {
        computedTier: t.computedTier,
        forcedTier: t.forcedTier ?? undefined,
        saleValue: t.saleValue ?? 0,
        store: {
          id: t.storeId,
          customerStoreId: store?.customerStoreId ?? '',
          code: store?.code ?? '',
          name: store?.name ?? ` - Unknown store ${t.storeId} -`,
        },
        percentage: (idx + 1) / withIndex.length,
      };
    });
  }, [storesData?.object, tiers]);

  const columns = useMemo(() => {
    const storeColumns = storeColumnsProvider<StoreTierRow>({
      [EntityColumns.ID]: { width: '12rem' },
      [EntityColumns.CODE]: { width: '8rem' },
    });

    const saleValueColumn: YColumnsType<StoreTierRow> = {
      key: 'saleValue',
      dataIndex: 'saleValue',
      title: `Sale value [${currency}]`,
      width: '12rem',
      render: (value: number) => formatNumber(value, 0),
      align: 'right',
    };

    const computedTierColumn: YColumnsType<StoreTierRow> = {
      key: 'computedTier',
      dataIndex: 'computedTier',
      title: 'Tier',
      width: '20rem',
      render: (value, record) => (
        <StoreTierSetter
          tier={{
            storeId: record.store.id,
            computedTier: record.computedTier,
            forcedTier: record.forcedTier,
          }}
        />
      ),
    };

    const percentageColumn: YColumnsType<StoreTierRow> = {
      key: 'percentage',
      dataIndex: 'percentage',
      title: '[%]',
      width: '5rem',
      render: (value: number) => formatNumber(value * 100, 1),
      align: 'right',
    };

    return [...storeColumns, saleValueColumn, computedTierColumn, percentageColumn];
  }, [currency, storeColumnsProvider]);

  const forceTiersToAllStores = useCallback(() => {
    if (modificationEnabled) {
      forceTiers({
        asoScenarioId,
        payload: { forcedTier: bulkTier },
      });
    }
  }, [asoScenarioId, bulkTier, forceTiers, modificationEnabled]);

  const resetTiersOnAllStores = useCallback(() => {
    if (modificationEnabled) {
      forceTiers({
        asoScenarioId,
        payload: { forcedTier: null },
      });
    }
  }, [asoScenarioId, forceTiers, modificationEnabled]);

  return (
    <Column $gap="1rem">
      {!isErrorTiers && (
        <Space style={{ marginBottom: '1rem' }}>
          <StoreTierSelector
            value={bulkTier}
            onChange={setBulkTier}
            disabled={isLocked || isForcing || isFetchingTiers}
            displayTooltip={true}
          />
          <Button
            type="primary"
            disabled={!asoScenarioId || isForcing || isFetchingTiers || isLocked}
            onClick={forceTiersToAllStores}
          >
            Set all stores
          </Button>
          <AsoTooltip tooltipKey="setAllStores" />
          <Button
            disabled={!asoScenarioId || isForcing || isFetchingTiers || isLocked}
            onClick={resetTiersOnAllStores}
          >
            Reset all stores
          </Button>
          <AsoTooltip tooltipKey="resetAllStores" />
        </Space>
      )}

      {(isFetchingTiers || isForcing) && (
        <Text>
          <Spinner />
          Loading store tiers…
        </Text>
      )}
      {isErrorTiers && <Text $type="danger">Failed to load store tiers.</Text>}
      {!isFetchingTiers && !isErrorTiers && data.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No stores" />
      )}
      {!isFetchingTiers && !isForcing && !isErrorTiers && data.length > 0 && (
        <DataTable<StoreTierRow>
          showBorder={true}
          showHeader={true}
          id="aso-store-tiers"
          loading={isFetchingTiers}
          columns={columns}
          dataSource={data}
          size="small"
          pagination={{ pageSize: 15, showSizeChanger: false, position: ['bottomLeft'] }}
          rowKey={r => String(r.store.id)}
        />
      )}
    </Column>
  );
};

export default AsoStoreTierTable;
